import axios from 'axios';
import ErrorService from '../core/error-handling/ErrorService';
import i18n from '@/i18n';

// Helper para traducciones del namespace auth
const ta = (key, opts) => i18n.t(key, { ns: 'auth', ...opts });

// Función local para obtener API URL (no usar import de config/api porque se minifica mal)
function getApiUrl() {
  const hostname = window.location.hostname;

  // Railway: Forzar HTTPS
  if (hostname.endsWith('.railway.app')) {
    return 'https://smarthub-backend-production.up.railway.app';
  }

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8010';
  }

  // Otros entornos: usar mismo protocolo que el frontend
  const protocol = window.location.protocol;
  return `${protocol}//${hostname}:8010`;
}

class AuthService {
  constructor() {
    this.token = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.isRefreshing = false;
    this.isRedirecting = false;
    this._pendingRequests = []; // queue de requests esperando el refresh
    this.setupInterceptors();
  }

  _resolvePending() {
    this._pendingRequests.forEach(({ resolve }) => resolve());
    this._pendingRequests = [];
  }

  _rejectPending(error) {
    this._pendingRequests.forEach(({ reject }) => reject(error));
    this._pendingRequests = [];
  }

  setupInterceptors() {
    // Request interceptor para agregar token
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor para manejar token expirado
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Solo intentar refresh si:
        // 1. Es un 401
        // 2. No es una petición de login o refresh
        // 3. No hemos intentado antes
        // 4. Tenemos un refresh token
        if (error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url.includes('/auth/login') &&
            !originalRequest.url.includes('/auth/refresh') &&
            !originalRequest.url.includes('/auth/logout') &&
            this.getRefreshToken()) {

          // Si ya hay un refresh en curso, encolar esta request y esperar
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this._pendingRequests.push({ resolve, reject });
            }).then(() => {
              originalRequest.headers.Authorization = `Bearer ${this.getToken()}`;
              originalRequest._retry = true;
              return axios(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            await this.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${this.getToken()}`;
            this.isRefreshing = false;
            this._resolvePending();
            return axios(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            this._rejectPending(refreshError);
            // Solo limpiar tokens y redirigir si NO estamos en la página de login
            this.clearTokens();
            this.clearUser();

            // Solo redirigir si no estamos ya en login
            if (!this.isRedirecting && !window.location.pathname.includes('/login')) {
              this.isRedirecting = true;
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        // Si es 401 y NO tenemos refresh token
        if (error.response?.status === 401 && !this.getRefreshToken()) {
          // If impersonating, restore original session instead of going to login
          if (this.isImpersonating()) {
            this.stopImpersonation().then(() => {
              window.location.href = '/configuracion/usuarios';
            });
            return Promise.reject(error);
          }

          this.clearTokens();
          this.clearUser();

          // Evitar múltiples redirects simultáneos
          if (!this.isRedirecting && !window.location.pathname.includes('/login')) {
            this.isRedirecting = true;
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async login(email, password) {
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/login`, {
        email,
        password
      });

      const { tokens, user } = response.data;

      // Guardar tokens
      this.setTokens(tokens.access_token, tokens.refresh_token);

      // Solo guardamos temporalmente en memoria, NO en localStorage
      this.setUser(user);

      return { success: true, user };
    } catch (error) {
      ErrorService.logError(error, 'AuthService.login');

      // Manejar mensajes específicos de login primero
      let errorMessage = ta('errors.invalidCredentials');

      if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
        const detail = error.response.data.detail.toLowerCase();

        if (detail.includes('invalid') ||
            detail.includes('credential') ||
            detail.includes('unauthorized')) {
          errorMessage = ta('errors.invalidCredentials');
        } else if (detail.includes('locked') || detail.includes('bloqueada')) {
          // Extraer tiempo restante si está disponible
          const minutesMatch = detail.match(/(\d+)\s*minutos?/);
          if (minutesMatch) {
            errorMessage = ta('errors.accountLockedMinutes', { minutes: minutesMatch[1] });
          } else {
            errorMessage = ta('errors.accountLockedGeneric');
          }
        } else if (detail.includes('attempts remaining') || detail.includes('intentos restantes')) {
          // Extraer número de intentos restantes
          const attemptsMatch = detail.match(/(\d+)\s*(attempts|intentos)/);
          if (attemptsMatch) {
            errorMessage = ta('errors.invalidCredentialsWithAttempts', { attempts: attemptsMatch[1] });
          } else {
            errorMessage = error.response.data.detail;
          }
        } else {
          // Usar el helper para otros errores
          errorMessage = this.formatAuthError(error, ta('errors.invalidCredentials'));
        }
      } else {
        // Usar el helper para errores de servidor, red, etc.
        errorMessage = this.formatAuthError(error, ta('errors.invalidCredentials'));
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async loginWithMicrosoft(idToken) {
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/microsoft`, {
        id_token: idToken
      });

      const { tokens, user } = response.data;

      this.setTokens(tokens.access_token, tokens.refresh_token);
      this.setUser(user);

      return { success: true, user };
    } catch (error) {
      ErrorService.logError(error, 'AuthService.loginWithMicrosoft');

      let errorMessage = ta('errors.microsoftLoginFailed');
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        errorMessage = detail;
      } else if (error.response?.status === 401) {
        errorMessage = ta('errors.accountNotEnabled');
      } else {
        errorMessage = this.formatAuthError(error, errorMessage);
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async loginWithGoogle(idToken) {
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/google`, {
        id_token: idToken
      });

      const { tokens, user } = response.data;

      this.setTokens(tokens.access_token, tokens.refresh_token);
      this.setUser(user);

      return { success: true, user };
    } catch (error) {
      ErrorService.logError(error, 'AuthService.loginWithGoogle');

      let errorMessage = ta('errors.googleLoginFailed');
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        errorMessage = detail;
      } else if (error.response?.status === 401) {
        errorMessage = ta('errors.accountNotEnabled');
      } else {
        errorMessage = this.formatAuthError(error, errorMessage);
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async register(userData) {
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/register`, userData);

      const { tokens, user } = response.data;

      // Guardar tokens
      this.setTokens(tokens.access_token, tokens.refresh_token);

      // Solo guardamos temporalmente en memoria, NO en localStorage
      this.setUser(user);

      return { success: true, user };
    } catch (error) {
      // Usar el mismo helper de formateo de errores que login
      const errorMessage = this.formatAuthError(error, ta('errors.unexpectedRegister'));

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Helper para formatear errores de autenticación
  formatAuthError(error, defaultMessage) {
    let errorMessage = defaultMessage || ta('errors.unexpectedLogin');

    if (error.response?.data) {
      const data = error.response.data;

      // FastAPI validation error format
      if (Array.isArray(data.detail)) {
        errorMessage = data.detail.map(err =>
          typeof err === 'string' ? err : err.msg || err.message || ta('errors.validationError')
        ).join(', ');
      } else if (typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else if (typeof data.detail === 'object' && data.detail.msg) {
        errorMessage = data.detail.msg;
      } else if (data.message) {
        errorMessage = data.message;
      }
    } else if (error.response?.status >= 500) {
      // Error de servidor (500, 502, 503, etc.)
      const status = error.response.status;
      if (status === 502 || status === 503) {
        errorMessage = ta('errors.serverUnavailable');
      } else if (status === 500) {
        errorMessage = ta('errors.serverInternalError');
      } else {
        errorMessage = ta('errors.serverErrorGeneric', { status });
      }
    } else if (!error.response) {
      // Error de red - sin respuesta del servidor
      if (error.code === 'ECONNABORTED') {
        errorMessage = ta('errors.connectionTimeout');
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = ta('errors.networkError');
      } else if (error.message?.toLowerCase().includes('network')) {
        errorMessage = ta('errors.networkGenericError');
      } else {
        errorMessage = ta('errors.serverConnectionError');
      }
    }

    return errorMessage;
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error(ta('errors.noRefreshToken'));
    }
    
    try {
      const response = await axios.post(`${getApiUrl()}/api/auth/refresh`, {
        refresh_token: refreshToken
      });

      // El backend devuelve access_token y refresh_token directamente
      const { access_token, refresh_token: newRefreshToken } = response.data;

      // Actualizar ambos tokens
      if (newRefreshToken) {
        this.setTokens(access_token, newRefreshToken);
      } else {
        this.setToken(access_token);
      }

      return access_token;
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const token = this.getToken();
      if (!token) {
        return null;
      }

      // SIEMPRE obtenemos datos frescos del backend
      const [userResponse, permissionsResponse, registrationPermissionsResponse] = await Promise.all([
        axios.get(`${getApiUrl()}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        axios.get(`${getApiUrl()}/api/permissions/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        axios.get(`${getApiUrl()}/api/auth/my-permissions`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(err => {
          console.warn('[AuthService] Error obteniendo registration permissions:', err);
          return { data: null };
        })
      ]);

      if (userResponse.data) {
        // Combinar datos de usuario con sus permisos
        const userData = {
          ...userResponse.data,
          permissions: permissionsResponse.data.permissions || [],
          userPermissions: registrationPermissionsResponse.data || null
        };

        // Solo guardamos temporalmente en memoria
        this.setUser(userData);
        return userData;
      }

      return null;
    } catch (error) {
      ErrorService.logError(error, 'AuthService.getCurrentUser');
      // Token inválido: limpiar y redirigir inmediatamente
      if (error.response?.status === 401) {
        this.clearTokens();
        this.clearUser();

        if (!this.isRedirecting && !window.location.pathname.includes('/login')) {
          this.isRedirecting = true;
          window.location.href = '/login';
        }
        return null;
      }
      // Error de red (backend reiniciando, sin conexión, etc.): propagar la excepción
      // para que el caller decida. No desautenticar por errores transitorios.
      throw error;
    }
  }

  async logout() {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await axios.post(`${getApiUrl()}/api/auth/logout`, {
          refresh_token: refreshToken
        });
      }
    } catch (error) {
      ErrorService.logError(error, 'AuthService.logout');
    } finally {
      this.clearTokens();
      this.clearUser();
    }
  }

  // Token management
  setTokens(accessToken, refreshToken) {
    this.token = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  setToken(accessToken) {
    this.token = accessToken;
    localStorage.setItem('access_token', accessToken);
  }

  getToken() {
    return this.token || localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return this.refreshToken || localStorage.getItem('refresh_token');
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // --- Impersonation ---

  async startImpersonation(accountId) {
    try {
      const token = this.getToken();
      const response = await axios.post(
        `${getApiUrl()}/api/admin/impersonate/${accountId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { access_token, impersonated_user } = response.data;

      // Save original tokens in sessionStorage (survives page refresh, not tab close)
      sessionStorage.setItem('original_access_token', this.getToken());
      sessionStorage.setItem('original_refresh_token', this.getRefreshToken() || '');
      sessionStorage.setItem('impersonating', 'true');
      sessionStorage.setItem('impersonated_user', JSON.stringify(impersonated_user));

      // Switch to impersonation token (no refresh token for impersonation)
      this.token = access_token;
      localStorage.setItem('access_token', access_token);
      this.refreshToken = null;
      localStorage.removeItem('refresh_token');

      return { success: true, impersonated_user };
    } catch (error) {
      ErrorService.logError(error, 'AuthService.startImpersonation');
      const detail = error.response?.data?.detail || ta('errors.impersonationStartError');
      return { success: false, error: detail };
    }
  }

  async stopImpersonation() {
    try {
      const impersonatedUser = this.getImpersonatedUser();
      const impersonatedAccountId = impersonatedUser?.id;

      // Restore original tokens
      const originalAccessToken = sessionStorage.getItem('original_access_token');
      const originalRefreshToken = sessionStorage.getItem('original_refresh_token');

      if (originalAccessToken) {
        this.setTokens(originalAccessToken, originalRefreshToken || '');
      }

      // Clear impersonation state
      sessionStorage.removeItem('original_access_token');
      sessionStorage.removeItem('original_refresh_token');
      sessionStorage.removeItem('impersonating');
      sessionStorage.removeItem('impersonated_user');

      // Notify backend for audit log (best effort)
      if (impersonatedAccountId) {
        try {
          await axios.post(
            `${getApiUrl()}/api/admin/impersonate/stop`,
            { impersonated_account_id: impersonatedAccountId },
            { headers: { Authorization: `Bearer ${this.getToken()}` } }
          );
        } catch {
          // Non-critical - audit log is nice to have
        }
      }

      return { success: true };
    } catch (error) {
      ErrorService.logError(error, 'AuthService.stopImpersonation');
      return { success: false, error: ta('errors.impersonationStopError') };
    }
  }

  isImpersonating() {
    return sessionStorage.getItem('impersonating') === 'true';
  }

  getImpersonatedUser() {
    try {
      const data = sessionStorage.getItem('impersonated_user');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  // User management - NO guardamos user data en localStorage
  setUser(user) {
    // Solo guardamos en memoria temporalmente
    this.currentUser = user;
  }

  getUser() {
    // Retornamos el usuario en memoria
    const user = this.currentUser || null;
    return user;
  }

  clearUser() {
    // Limpiamos de memoria
    this.currentUser = null;
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  hasRole(role) {
    const user = this.getUser();
    if (!user) return false;
    
    // Check in active profile first
    if (user.active_profile?.role?.name === role) {
      return true;
    }
    
    // Check in all profiles
    if (user.profiles) {
      return user.profiles.some(profile => 
        profile.role?.name === role && profile.is_active
      );
    }
    
    // Legacy check for old user structure
    return user?.roles?.includes(role) || false;
  }

  hasPermission(permission) {
    const user = this.getUser();
    if (!user) return false;

    // Check in permissions array (from /api/permissions/me)
    if (!user.permissions || !Array.isArray(user.permissions)) {
      return false;
    }

    // Direct match
    if (user.permissions.includes(permission)) {
      return true;
    }

    // Check hierarchical permissions
    // e.g., if user has 'academic', they have 'academic.subjects.view'
    const parts = permission.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const parentPerm = parts.slice(0, i).join('.');
      if (user.permissions.includes(parentPerm)) {
        return true;
      }
    }

    return false;
  }
}

const authService = new AuthService();
export default authService;
